/**

 * 智慧无限 DK87/CX87 自定义灯效：led_define_tab（384 字节）与 UI 网格互转。

 * 协议命令 0x19 读 / 0x1a 写；6 行 × 17 列，每格 3 字节 RGB。

 */

import type { CustomLightingData } from "@eweadn-qiankun/shared-types";

import { resolveZhwxDk75SingleProtocolCell } from "./zhwxDk75SingleLayout";

import {
  ZHWX_MATRIX_COLS,
  ZHWX_MATRIX_ROWS,
  ZHWX_LED_DEFINE_TAB_SIZE,
  zhwxMatrixIndex,
} from "./zhwxDk87Layout";

import { resolveZhwxLayoutProfile } from "./zhwxLayoutProfile";

type ZhwxCustomLedCell = CustomLightingData[number][number];

export { ZHWX_LED_DEFINE_TAB_SIZE };

/** RGB 是否视为「已自定义设色」（固件默认/清除为 0,0,0） */

export function isZhwxCustomLedRgb(r: number, g: number, b: number): boolean {
  return (r | g | b) !== 0;
}

/** led_define_tab 单格 → UI 单元 */

export function zhwxLedRgbToCustomCell(
  r: number,

  g: number,

  b: number
): { R: number; G: number; B: number; isCustom: boolean } {
  const R = r & 0xff;

  const G = g & 0xff;

  const B = b & 0xff;

  return {
    R,

    G,

    B,

    isCustom: isZhwxCustomLedRgb(R, G, B),
  };
}

/** 384 字节 led_define_tab → 6×17 CustomLightingData（协议坐标） */

export function zhwxLedDefineTabToCustomGrid(
  led: Uint8Array
): CustomLightingData {
  const grid: CustomLightingData = [];

  for (let row = 0; row < ZHWX_MATRIX_ROWS; row++) {
    const rowData: CustomLightingData[number] = [];

    for (let col = 0; col < ZHWX_MATRIX_COLS; col++) {
      const base = zhwxMatrixIndex(row, col) * 3;

      rowData.push(
        zhwxLedRgbToCustomCell(
          led[base] ?? 0,

          led[base + 1] ?? 0,

          led[base + 2] ?? 0
        )
      );
    }

    grid.push(rowData);
  }

  return grid;
}

/** CustomLightingData → 384 字节（可基于现有表增量覆盖） */

export function zhwxCustomGridToLedDefineTab(
  grid: CustomLightingData,

  base?: Uint8Array
): Uint8Array {
  const next =
    base && base.length >= ZHWX_LED_DEFINE_TAB_SIZE
      ? new Uint8Array(base.slice(0, ZHWX_LED_DEFINE_TAB_SIZE))
      : new Uint8Array(ZHWX_LED_DEFINE_TAB_SIZE);

  for (let row = 0; row < ZHWX_MATRIX_ROWS; row++) {
    for (let col = 0; col < ZHWX_MATRIX_COLS; col++) {
      const cell = grid[row]?.[col];

      const offset = zhwxMatrixIndex(row, col) * 3;

      next[offset] = (cell?.R ?? 0) & 0xff;

      next[offset + 1] = (cell?.G ?? 0) & 0xff;

      next[offset + 2] = (cell?.B ?? 0) & 0xff;
    }
  }

  return next;
}

/** 批量 patch 矩阵坐标（含清除：写 0,0,0） */

export function zhwxPatchCustomGridCells(
  grid: CustomLightingData,

  patches: ReadonlyArray<{
    row: number;

    col: number;

    R: number;

    G: number;

    B: number;
  }>
): CustomLightingData {
  const next = grid.map((row) => row.map((cell) => ({ ...cell })));

  for (const patch of patches) {
    const { row, col, R, G, B } = patch;

    if (
      row < 0 ||
      row >= ZHWX_MATRIX_ROWS ||
      col < 0 ||
      col >= ZHWX_MATRIX_COLS
    ) {
      continue;
    }

    if (!next[row]) next[row] = [];

    next[row][col] = zhwxLedRgbToCustomCell(R, G, B);
  }

  return next;
}

/** 清除整表自定义色（全 0） */

export function zhwxCreateEmptyCustomGrid(): CustomLightingData {
  return zhwxLedDefineTabToCustomGrid(new Uint8Array(ZHWX_LED_DEFINE_TAB_SIZE));
}

function cloneZhwxCustomGrid(grid: CustomLightingData): CustomLightingData {
  return grid.map((row) => row.map((cell) => ({ ...cell })));
}

function readZhwxCustomLedCell(
  grid: CustomLightingData,

  row: number,

  col: number
): ZhwxCustomLedCell {
  return (
    grid[row]?.[col] ?? {
      R: 0,

      G: 0,

      B: 0,

      isCustom: false,
    }
  );
}

/**

 * DK75 Single：0x19/0x1a 与 0x16 键矩阵同协议坐标系，非公版 UI 视觉坐标。

 * 例：UI (1,6) 数字 6 → 协议 (1,4)；写 UI 列号会导致实体键偏 2 格（6/7 亮到 8/9）。

 */

export function remapZhwxDk75SingleCustomLedGridDeviceToUi(
  protocolGrid: CustomLightingData
): CustomLightingData {
  const profile = resolveZhwxLayoutProfile("dk75Single");

  const next = zhwxCreateEmptyCustomGrid();

  for (let uiRow = 0; uiRow < ZHWX_MATRIX_ROWS; uiRow++) {
    for (let uiCol = 0; uiCol < ZHWX_MATRIX_COLS; uiCol++) {
      if (!profile.isPhysicalCell(uiRow, uiCol)) continue;

      const protocolCell = resolveZhwxDk75SingleProtocolCell(uiRow, uiCol);

      if (!protocolCell) continue;

      if (!next[uiRow]) next[uiRow] = [];

      next[uiRow][uiCol] = {
        ...readZhwxCustomLedCell(
          protocolGrid,

          protocolCell.row,

          protocolCell.col
        ),
      };
    }
  }

  return next;
}

/** DK75 Single：公版 UI 网格 → 协议 led_define 网格（写入 0x1a 前） */

export function remapZhwxDk75SingleCustomLedGridUiToDevice(
  uiGrid: CustomLightingData,

  baseProtocolGrid?: CustomLightingData
): CustomLightingData {
  const profile = resolveZhwxLayoutProfile("dk75Single");

  const next = cloneZhwxCustomGrid(
    baseProtocolGrid ?? zhwxCreateEmptyCustomGrid()
  );

  for (let uiRow = 0; uiRow < ZHWX_MATRIX_ROWS; uiRow++) {
    for (let uiCol = 0; uiCol < ZHWX_MATRIX_COLS; uiCol++) {
      if (!profile.isPhysicalCell(uiRow, uiCol)) continue;

      const protocolCell = resolveZhwxDk75SingleProtocolCell(uiRow, uiCol);

      if (!protocolCell) continue;

      const cell = uiGrid[uiRow]?.[uiCol];

      if (!cell) continue;

      if (!next[protocolCell.row]) next[protocolCell.row] = [];

      next[protocolCell.row][protocolCell.col] = { ...cell };
    }
  }

  return next;
}
